import { z } from 'zod';
import { cnpjValido } from './empresa.js';
import { cuid } from './primitives.js';

/**
 * Parceiro de Negocio (Sessao 30): cadastro unico PF | PJ com PAPEIS (cliente, fornecedor, colaborador, terceirizado, empresa).
 * Os dados do papel ficam nos contratos de Clientes, Fornecedores e Pessoas; aqui estao a identidade, os enderecos e os contatos.
 */
export const PAPEIS_PARCEIRO = ['CLIENTE', 'FORNECEDOR', 'COLABORADOR', 'TERCEIRIZADO', 'EMPRESA'] as const;
export const papelParceiroSchema = z.enum(PAPEIS_PARCEIRO);
export type PapelParceiro = z.infer<typeof papelParceiroSchema>;
export const ROTULO_PAPEL_PARCEIRO: Record<PapelParceiro, string> = {
  CLIENTE: 'Cliente',
  FORNECEDOR: 'Fornecedor',
  COLABORADOR: 'Colaborador',
  TERCEIRIZADO: 'Terceirizado',
  EMPRESA: 'Empresa',
};

export const naturezaParceiroSchema = z.enum(['FISICA', 'JURIDICA']);
export type NaturezaParceiro = z.infer<typeof naturezaParceiroSchema>;

export const CONTRIBUINTES_ICMS = ['CONTRIBUINTE', 'ISENTO', 'NAO_CONTRIBUINTE'] as const;
export const contribuinteIcmsSchema = z.enum(CONTRIBUINTES_ICMS);
export type ContribuinteIcms = z.infer<typeof contribuinteIcmsSchema>;
export const ROTULO_CONTRIBUINTE_ICMS: Record<ContribuinteIcms, string> = {
  CONTRIBUINTE: 'Contribuinte',
  ISENTO: 'Isento',
  NAO_CONTRIBUINTE: 'Não contribuinte',
};

export const TIPOS_ENDERECO = ['COMERCIAL', 'RESIDENCIAL', 'COBRANCA', 'ENTREGA', 'OUTRO'] as const;
export const tipoEnderecoSchema = z.enum(TIPOS_ENDERECO);
export type TipoEndereco = z.infer<typeof tipoEnderecoSchema>;
export const ROTULO_TIPO_ENDERECO: Record<TipoEndereco, string> = {
  COMERCIAL: 'Comercial',
  RESIDENCIAL: 'Residencial',
  COBRANCA: 'Cobrança',
  ENTREGA: 'Entrega',
  OUTRO: 'Outro',
};

export function cpfDigitosValidos(cpf: string): boolean {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  const dig = (n: number): number => {
    let soma = 0;
    for (let i = 0; i < n; i += 1) soma += Number(cpf[i]) * (n + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dig(9) === Number(cpf[9]) && dig(10) === Number(cpf[10]);
}

/** CPF (11) ou CNPJ (14) so com digitos e digito verificador correto. Aritmetica de cadastro, nao financeira. */
export function documentoValido(documento: string): boolean {
  return documento.length === 11 ? cpfDigitosValidos(documento) : documento.length === 14 ? cnpjValido(documento) : false;
}

/** Tira a mascara: "11.222.333/0001-81" -> "11222333000181". */
export function soDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** Documento do parceiro: so digitos, CPF ou CNPJ, com digito verificador. */
export const documentoParceiroSchema = z
  .string()
  .regex(/^(\d{11}|\d{14})$/, 'Documento só com dígitos: CPF (11) ou CNPJ (14)')
  .refine(documentoValido, 'CPF ou CNPJ inválido (confira os dígitos)');

export const enderecoParceiroSchema = z.object({
  tipo: tipoEnderecoSchema.default('COMERCIAL'),
  principal: z.boolean().default(false),
  cep: z.string().regex(/^\d{8}$/, 'CEP com 8 dígitos').optional(),
  logradouro: z.string().trim().min(1).max(200).optional(),
  numero: z.string().trim().min(1).max(20).optional(),
  complemento: z.string().trim().min(1).max(100).optional(),
  bairro: z.string().trim().min(1).max(100).optional(),
  municipio: z.string().trim().min(1).max(100).optional(),
  /** Codigo IBGE do municipio (7 digitos). */
  codigoIbge: z.string().regex(/^\d{7}$/, 'Código IBGE com 7 dígitos').optional(),
  uf: z.string().regex(/^[A-Z]{2}$/, 'UF com 2 letras maiúsculas').optional(),
});
export type EnderecoParceiroDto = z.infer<typeof enderecoParceiroSchema>;

export const contatoParceiroSchema = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do contato').max(120),
  cargo: z.string().trim().min(1).max(100).optional(),
  email: z.string().trim().max(200).pipe(z.email('E-mail inválido')).optional(),
  telefone: z.string().trim().min(1).max(40).optional(),
  observacao: z.string().trim().min(1).max(200).optional(),
});
export type ContatoParceiroDto = z.infer<typeof contatoParceiroSchema>;

/** Um endereco principal no maximo. */
export function umPrincipal(enderecos: ReadonlyArray<{ principal?: boolean | undefined }>, ctx: z.RefinementCtx): void {
  if (enderecos.filter((e) => e.principal).length > 1) ctx.addIssue({ code: 'custom', path: ['enderecos'], message: 'Só um endereço pode ser o principal' });
}

/** Campos do parceiro que Clientes, Fornecedores e Pessoas aceitam alem dos proprios (todos opcionais). */
export const dadosParceiroShape = {
  fantasia: z.string().trim().min(1).max(200).optional(),
  ie: z.string().trim().min(1).max(30).optional(),
  contribuinteIcms: contribuinteIcmsSchema.optional(),
  observacoes: z.string().trim().min(1).max(2000).optional(),
  enderecos: z.array(enderecoParceiroSchema).max(10).optional(),
  contatos: z.array(contatoParceiroSchema).max(20).optional(),
};
/** Versao para atualizar: o que pode ser limpo aceita null. */
export const dadosParceiroAtualizarShape = {
  fantasia: dadosParceiroShape.fantasia.nullable().optional(),
  ie: dadosParceiroShape.ie.nullable().optional(),
  contribuinteIcms: contribuinteIcmsSchema.nullable().optional(),
  observacoes: dadosParceiroShape.observacoes.nullable().optional(),
  enderecos: dadosParceiroShape.enderecos,
  contatos: dadosParceiroShape.contatos,
};

export interface EnderecoParceiroResponse {
  id: string;
  tipo: TipoEndereco;
  principal: boolean;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: string | null;
  codigoIbge: string | null;
  uf: string | null;
}
export interface ContatoParceiroResponse {
  id: string;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  observacao: string | null;
}
/** O que as respostas de Clientes, Fornecedores e Pessoas ganham do parceiro. */
export interface DadosParceiroResponse {
  parceiroId: string;
  natureza: NaturezaParceiro;
  fantasia: string | null;
  ie: string | null;
  contribuinteIcms: ContribuinteIcms | null;
  observacoes: string | null;
  papeis: PapelParceiro[];
  enderecos: EnderecoParceiroResponse[];
  contatos: ContatoParceiroResponse[];
}

/** GET /parceiros/por-documento?documento= : "este CPF/CNPJ ja esta cadastrado" (para oferecer usar o parceiro existente). */
export const parceiroPorDocumentoSchema = z.object({ documento: z.string().transform(soDigitos).pipe(documentoParceiroSchema) });
export type ParceiroPorDocumentoQuery = z.infer<typeof parceiroPorDocumentoSchema>;
export interface ParceiroResumoResponse {
  id: string;
  nome: string;
  fantasia: string | null;
  documento: string | null;
  natureza: NaturezaParceiro;
  papeis: PapelParceiro[];
  /** Id do cliente, fornecedor ou colaborador deste parceiro (para abrir a tela do papel). */
  clienteId: string | null;
  fornecedorId: string | null;
  pessoaId: string | null;
}
export interface ParceiroPorDocumentoResponse {
  parceiro: ParceiroResumoResponse | null;
}

/** POST /{clientes|fornecedores}/:id/papel-... nao existe: o segundo papel nasce criando o cadastro do papel com `parceiroId`. */
export const parceiroIdSchema = z.object({ parceiroId: cuid });

/** Reforma/IBS: a UF do endereco principal do cliente e a UF de destino padrao do orcamento. */
export function ufDoEnderecoPrincipal(enderecos: ReadonlyArray<{ principal: boolean; uf: string | null }>): string | null {
  return (enderecos.find((e) => e.principal) ?? enderecos[0])?.uf ?? null;
}

/** ViaCEP / IBGE (preenchimento pelo navegador): formato minimo usado. */
export interface RespostaViaCep {
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  ibge?: string;
  erro?: boolean | string;
}
export interface MunicipioIbge {
  id: number;
  nome: string;
  uf: string;
}
